/**
 * #754 — the seam between the dashboard's own reading controls and the pure
 * #754/#902 discovery selection (`discovery.ts` / `discoveryCatalog.ts`).
 *
 * Everything here is a pure translation. The board owns the store reads and the
 * rendering; this module answers three questions about them:
 *
 *   1. what `DiscoveryReading` does the trader's current toolbar state mean,
 *   2. which sports the board must read to satisfy it, and
 *   3. what the result set honestly covers — including what it does not.
 *
 * It deliberately holds no state, reads no store and touches no navigation,
 * arming, ticket or order surface. A search is a way of LOOKING at the admitted
 * catalog; it never becomes an executable target on its own.
 */
import type { DiscoveryGroup, DiscoveryMode, DiscoveryReading, WeekDefinition } from './discovery';
import type { DiscoveryRow } from './discoveryCatalog';
import type { FuturesBoard } from './futuresDetail';
import {
  MARKET_TYPE_TABS, MULTI_OUTCOME_SPORTS, SPORT_TABS,
  type DateFilter, type GameEventRow, type GameRow, type MarketType,
  type MultiOutcomeEvent, type Sport, type StatusFilter,
} from './types';
import { isoToMdy, type SlateWindow } from './weeklySlate';

/**
 * The provisional week policy, in ONE place.
 *
 * `discoveryRange` takes the definition as an argument precisely so product
 * confirmation moves a constant rather than a calculation (`discovery.ts:84-86`).
 * Monday–Sunday is the proposed display default and is still an open product
 * question; it is explicitly NOT football's Tuesday–Monday slate week, which
 * belongs to `weeklySlate.ts` and must not leak into discovery.
 *
 * `DiscoveryModeControls` (#902) is what selects a non-manual mode; the board
 * passes this definition to `selectDiscoveryGroups` for it.
 */
export const DISCOVERY_WEEK: WeekDefinition = 'calendar-monday';

/**
 * How many eligible groups are on screen before the trader asks for more.
 *
 * Mirrors `selectDiscoveryGroups`' own default; it is named here because the
 * board raises it, and because 20 is a presentation proposal still open for
 * confirmation rather than a property of the selection. It is a page size, not
 * a cap: `total` always counts every eligible group and every one of them stays
 * reachable.
 */
export const DISCOVERY_PAGE_SIZE = 20;

/** Futures boards exist for sports with no game inventory, so the catalog's
 *  sport list is the board tabs plus golf — not `SPORT_TABS` alone. */
export const DISCOVERY_SPORTS: readonly Sport[] = [...SPORT_TABS.map((tab) => tab.id), 'golf'];

/**
 * Is a committed query actually narrowing the board?
 *
 * Whitespace is not a search. `commitDiscoveryQuery` normalises on the way in,
 * but a stored record from an older session is untrusted, so this asks the
 * question rather than trusting the field's shape.
 */
export function isDiscoveryActive(query: string): boolean {
  return query.trim().length > 0;
}

/**
 * A board with no catalog behind it says so rather than searching silently.
 *
 * Every market type the toolbar can SELECT is searchable: spread/total joined
 * moneyline and futures once `getGameEvents` gained the version-stamped result
 * cache `getGames` already had (the blocker was cost, not shape). What remains
 * are the legacy `'spread'` / `'total'` values — no longer board tabs, but
 * still accepted by `readingView`'s parser, so a working view stored before
 * those tabs merged can still arrive here and must get an honest answer.
 */
export const DISCOVERY_UNSUPPORTED_NOTE = 'Search isn’t available on this board yet.';

/** Whether a committed query can narrow this board at all. */
export function isSearchableMarketType(marketType: MarketType): boolean {
  return marketType === 'moneyline' || marketType === 'series' || marketType === 'spread-total';
}

/**
 * May this board hold a Today / This week MODE?
 *
 * A mode is a date window taken across sports, so it needs a family whose
 * groups carry a date the store itself admits them by. Moneyline and
 * spread/total both do — `GameRow.admissionDate` and `GameEventRow`'s, which
 * are the same schedule day. Futures boards carry no start date at all
 * (`discoveryCatalog.ts` refuses to invent one from a year label), so a mode
 * there would empty the board by construction rather than scope it.
 *
 * Separate from `isSearchableMarketType`: a query is a text filter over
 * whatever the board admits, which needs no date at all.
 */
export function isModeEligibleMarketType(marketType: MarketType): boolean {
  return marketType === 'moneyline' || marketType === 'spread-total';
}

/** The dashboard toolbar state a discovery reading is derived from. */
export interface DashboardDiscoveryScope {
  /** The COMMITTED query — never the draft the trader is still typing. */
  query: string;
  mode: DiscoveryMode;
  marketType: MarketType;
  statusFilter: StatusFilter;
  /** The Kalshi-markets-only chip AS APPLIED — it is inert on some boards. */
  kalshiMarketsOnly: boolean;
  allSports: boolean;
  selectedSport: Sport;
  dateFilter: DateFilter;
  slate: SlateWindow;
}

/**
 * The trader's toolbar, expressed as the selection contract's reading.
 *
 * `manualSports` and `manualRange` are the SAVED scope, not the effective one:
 * `selectDiscoveryGroups` widens sports itself once a query or a non-manual
 * mode is present, and restores this scope the moment the query clears. Writing
 * the widened scope into them instead would make the clear gesture unable to
 * put the board back.
 */
export function discoveryReadingFor(scope: DashboardDiscoveryScope): DiscoveryReading {
  return {
    mode: scope.mode,
    query: scope.query,
    marketType: scope.marketType,
    status: scope.statusFilter,
    // The dashboard has no venue chip; the Kalshi-only toggle is that filter
    // wearing a different name, and discovery must obey it rather than quietly
    // returning the Polymarket rows the board is hiding.
    venue: scope.kalshiMarketsOnly ? 'kalshi' : 'all',
    manualSports: scope.allSports ? null : [scope.selectedSport],
    // 'all' means the trader took the date bound OFF. A window here would put
    // it back, so an unbounded board must produce a null range.
    manualRange: scope.dateFilter === 'today'
      ? { startIso: scope.slate.startIso, endIso: scope.slate.endIso }
      : null,
  };
}

/** Which sports each catalog family must be read for. */
export interface DiscoveryCatalogPlan {
  /** Binary away/home rows (`getGames`). */
  moneylineSports: readonly Sport[];
  /** N-outcome events (`getMultiOutcomeEvents`) — soccer today. */
  multiSports: readonly Sport[];
  /** Whole-event spread + total line rows (`getGameEvents`, period 'full'). */
  linesSports: readonly Sport[];
  /** Outright/futures boards (`getSeriesEvents` + `getFutureBoards`). */
  futuresSports: readonly Sport[];
}

/**
 * Sports that can carry a spread or total board.
 *
 * Golf is in `DISCOVERY_SPORTS` for its futures boards alone — it has no
 * away/home event to hang a line on — so reading it here would re-parse that
 * sport's whole ticker catalog for a family it never produces.
 */
export const DISCOVERY_LINE_SPORTS: readonly Sport[] = SPORT_TABS.map((tab) => tab.id);

/**
 * The sports to read, widened only while a search is committed.
 *
 * A committed query temporarily spans sports; everything else about the board
 * — market type, date window, status, venue — is retained. When no query is
 * committed the plan is exactly the board's existing selection, so the ordinary
 * view costs precisely what it costs today.
 *
 * Widening is per FAMILY because the families do not overlap: `getGames`
 * already returns nothing for multi-outcome and standalone sports, and the
 * multi-outcome path is soccer alone.
 */
export function discoveryCatalogPlan(input: {
  active: boolean;
  marketType: MarketType;
  /** The board's current binary-moneyline sports. */
  boardMoneylineSports: readonly Sport[];
  /** The board's current multi-outcome sport, when it is on one. */
  boardMultiSport: Sport | null;
  /** The board's single-sport selection, which futures and spread/total use. */
  boardSport: Sport;
}): DiscoveryCatalogPlan {
  const none: readonly Sport[] = [];
  if (input.marketType === 'series') {
    return {
      moneylineSports: none,
      multiSports: none,
      linesSports: none,
      futuresSports: input.active ? DISCOVERY_SPORTS : [input.boardSport],
    };
  }
  if (input.marketType === 'spread-total') {
    return {
      moneylineSports: none,
      multiSports: none,
      // Idle, this asks for NOTHING rather than for the board's own sport:
      // `SpreadsTotals` owns that read, including its #757 fall-forward, and a
      // second read of the same sport here would derive the same rows twice.
      // A committed query or a mode is what makes the board hand it rows.
      linesSports: input.active ? DISCOVERY_LINE_SPORTS : none,
      futuresSports: none,
    };
  }
  if (input.marketType !== 'moneyline') {
    // A legacy 'spread' / 'total' working view — no board, no catalog.
    return { moneylineSports: none, multiSports: none, linesSports: none, futuresSports: none };
  }
  return {
    moneylineSports: input.active
      ? DISCOVERY_SPORTS.filter((sport) => !MULTI_OUTCOME_SPORTS.has(sport))
      : input.boardMoneylineSports,
    multiSports: input.active
      ? [...MULTI_OUTCOME_SPORTS]
      : (input.boardMultiSport ? [input.boardMultiSport] : none),
    linesSports: none,
    futuresSports: none,
  };
}

/** Selected groups, back in the shapes their renderers take. */
export interface DiscoveryRenderSets {
  moneylines: GameRow[];
  multi: MultiOutcomeEvent[];
  lines: GameEventRow[];
  futures: FuturesBoard[];
}

/**
 * Split selected groups by family, preserving the selection's order.
 *
 * A single moneyline query can match binary games AND soccer events, which are
 * different renderers; the board shows both rather than picking one, so a
 * result is never silently dropped for having landed in the other table.
 *
 * The families cannot mix across market types — `selectDiscoveryGroups` admits
 * exactly one — so a spread/total board's results are `lines` alone and a
 * moneyline board's never include them. Cross-market-type results remain an
 * open confirmation, not something this split can smuggle in.
 */
export function splitDiscoveryResults(
  groups: readonly DiscoveryGroup<DiscoveryRow>[],
): DiscoveryRenderSets {
  const sets: DiscoveryRenderSets = { moneylines: [], multi: [], lines: [], futures: [] };
  for (const group of groups) {
    if (group.row.kind === 'moneyline') sets.moneylines.push(group.row.value);
    else if (group.row.kind === 'multi') sets.multi.push(group.row.value);
    else if (group.row.kind === 'lines') sets.lines.push(group.row.value);
    else if (group.row.kind === 'future') sets.futures.push(group.row.value);
  }
  return sets;
}

/** What the board tells the trader it just searched, and what it did not. */
export interface DiscoveryCoverage {
  /** The scope actually applied, in the toolbar's own words. */
  scope: string;
  /**
   * How much of the eligible set is on screen — null while a Today/This week
   * mode is on, because that mode's own controls state the counts beside the
   * dates they belong to, and one fact printed twice invites two readings.
   */
  counts: string | null;
  /** Known blind spots, stated rather than implied by an empty result. */
  gaps: readonly string[];
}

const STATUS_LABEL: Record<StatusFilter, string> = {
  active: 'Active',
  all: 'Any status',
  settled: 'Settled',
};

function marketTypeLabel(marketType: MarketType): string {
  return MARKET_TYPE_TABS.find((tab) => tab.id === marketType)?.label ?? marketType;
}

/**
 * The visible scope summary the acceptance criteria require, plus the honest
 * coverage limits.
 *
 * The gaps are not hedging. A trader who searches a player's name and sees
 * nothing has to be able to tell "no such market" from "this board cannot see
 * that kind of market", and only this line can tell them.
 */
export function discoveryCoverage(input: {
  query: string;
  /**
   * #902 — which control is speaking for the scope.
   *
   * In a Today/This week mode the dates, the all-sport widening and the counts
   * belong to `DiscoveryModeControls`, which names them exactly as the rows
   * were selected. What is left for this line is what the mode does NOT
   * change: the query and the filters that still apply underneath it.
   */
  mode: DiscoveryMode;
  marketType: MarketType;
  statusFilter: StatusFilter;
  dateFilter: DateFilter;
  slate: SlateWindow;
  allSports: boolean;
  sportLabel: string;
  kalshiMarketsOnly: boolean;
  total: number;
  shown: number;
  /**
   * #754 — why the searched window is not the one on the date pill, when the
   * board has fallen forward to the next day with games. Composed by
   * `upcomingSlate.ts`, which owns every phrasing of that fall-forward, so
   * this line and the board's own banner cannot drift apart.
   */
  fallbackNote?: string | null;
}): DiscoveryCoverage {
  const range = input.slate.startIso === input.slate.endIso
    ? isoToMdy(input.slate.startIso)
    : `${isoToMdy(input.slate.startIso)} – ${isoToMdy(input.slate.endIso)}`;
  const inMode = input.mode !== 'manual';
  const scope = [
    ...(input.query ? [`Search “${input.query}”`] : []),
    // A committed query spans sports by design; saying so is what keeps the
    // widened result set from reading as a broken sport filter. In a mode the
    // controls above say it, in words about the mode rather than the query.
    ...(inMode ? [] : [input.allSports ? 'all sports' : `${input.sportLabel} only`]),
    marketTypeLabel(input.marketType),
    STATUS_LABEL[input.statusFilter],
    // The manual date window is not the window a mode read over, so naming it
    // here would be naming a scope the rows did not come from.
    ...(inMode ? [] : [input.dateFilter === 'today' ? range : 'any date']),
    ...(input.kalshiMarketsOnly ? ['Kalshi only'] : []),
  ].join(' · ');

  const counts = inMode
    ? null
    : input.total === 0
      ? 'No matches'
      : input.shown >= input.total
        ? `${input.total} ${input.total === 1 ? 'match' : 'matches'}`
        : `Showing ${input.shown} of ${input.total}`;

  // Leads the gap list: a trader who cannot find a game they can SEE needs
  // this before any statement about which families are covered.
  const gaps: string[] = input.fallbackNote ? [`${input.fallbackNote}.`] : [];
  if (input.marketType === 'moneyline') {
    gaps.push('Golf match boards and player-prop markets are not in the overview catalog, '
      + 'so they cannot be found from here.');
  }
  if (input.marketType === 'spread-total') {
    // FIRST, and only under a mode: it is a correction to the sentence
    // `DiscoveryModeControls` has just printed. That component states the
    // ranking for every mode it serves ("biggest total traded volume first"),
    // which is vacuous on this board — no spread/total event carries that
    // figure at all — so the line that says what the order actually IS has to
    // sit directly beneath it rather than at the tail of a different sentence.
    // Its copy belongs to #902 and is not edited from here.
    if (input.mode !== 'manual') {
      gaps.push('No Spread/Total event carries a traded-volume figure, so the ranking '
        + 'above does not order these — they are in start-time order.');
    }
    // What this board's catalog is a catalog OF. `getGameEvents` is read at
    // period 'full', so the 1H and quarter ladders this board can itself show
    // under a period tab are not searchable.
    gaps.push('Only full-game spread and total lines are searchable here — period lines '
      + '(1H, quarters) and player-prop markets are not in the overview catalog.');
  }
  if (input.marketType === 'series') {
    // `discoveryCatalog.ts` gives futures no date and no whole-board status
    // rather than inventing them from prices or year labels, so the two
    // filters that would otherwise hide every board are worth naming.
    gaps.push('Futures boards carry no start date or settled state, so they appear only '
      + 'under Any status with the date filter off.');
  }
  if (!isSearchableMarketType(input.marketType)) gaps.push(DISCOVERY_UNSUPPORTED_NOTE);
  return { scope, counts, gaps };
}
